<template>
  <div class="wiyw critical-component-clear grid container">
    <div class="wiyw__list col xs4 l2-11">
      <template
        v-for="(block, index) in blocks"
        :key="`block-${liquid.sectionId}-${index}`"
      >
        <div
          :style="{'--grid-row': Math.floor(index / 3) + 1}"
          class="wiyw__item-wrap"
        >
          <div
            v-if="block.image"
            v-show="!isMobile"
            class="wiyw__item"
          >
            <div class="wiyw__featured-image">
              <responsive-image
                :image="block.image"
                :alt="block.title || ''"
                :lazyload="false"
                min-max="375-1000"
                ratio="375:500"
              />

              <div class="wiyw__details-button-wrap">
                <btn
                  class="wiyw__details-button"
                  :label="activeBlock === block.id ? $string('wiyw.hide_details') : $string('wiyw.show_details')"
                  class-label="text-label-xs"
                  modifiers="light small"
                  :show-icon="false"
                  @click.prevent="toggleDetailView(block.id)"
                />
              </div>
            </div>
          </div>

          <transition
            name="fade"
            :css="!isMobile"
          >
            <div
              v-show="activeBlock === block.id || isMobile"
              class="wiyw__item-detail grid"
            >
              <div class="wiyw__featured-image col xs4 l1-4">
                <responsive-image
                  :image="block.image"
                  :alt="block.title || ''"
                  block-class="wiyw"
                  min-max="375-1000"
                  ratio="375:500"
                  :lazyload="true"
                />

                <div class="wiyw__details-button-wrap is-visible">
                  <btn
                    class="wiyw__details-button"
                    :label="activeBlock === block.id ? $string('wiyw.hide_details') : $string('wiyw.show_details')"
                    class-label="text-label-xs"
                    modifiers="light small"
                    :show-icon="false"
                    @click="toggleDetailView(block.id)"
                  />
                </div>
              </div>

              <div
                class="wiyw__detail-content col xs4 l5-11"
                :class="{ [$class('open')]: activeBlock === block.id }"
              >
                <div
                  v-if="block.products && block.products.length"
                  class="wiyw__detail-products"
                >
                  <div
                    v-for="product in block.products"
                    :key="`product-${liquid.sectionId}-${index}-${product.index}`"
                    class="wiyw__detail-product"
                  >
                    <product-card
                      v-if="product.title"
                      :product="product"
                      :alternative-image="product.index === 0 ? block.product1Image : block.product2Image"
                      placement="wiyw"
                    />
                    <component
                      :is="product.url ? 'a' : 'div'"
                      v-else-if="product.index === 0 ? block.product1Image : block.product2Image"
                      :href="product.url"
                      target="_blank"
                      class="wiyw__detail-product-link"
                    >
                      <responsive-image
                        :image="product.index === 0 ? block.product1Image : block.product2Image"
                        :lazyload="false"
                        min-max="200-500"
                        ratio="4:5"
                      />
                    </component>
                  </div>
                </div>

                <div class="wiyw__detail-copy">
                  <p
                    class="wiyw__detail-title text-label-s"
                    v-text="block.title"
                  />

                  <div
                    v-if="block.text"
                    class="wiyw__detail-text text-body-3xs"
                    v-html="block.text"
                  />

                  <a
                    v-if="block.linkLabel && block.linkUrl"
                    class="wiyw__detail-link text-label-xs"
                    :href="block.linkUrl"
                    target="_blank"
                  >
                    <icon-instagram />
                    <span v-text="block.linkLabel" />
                  </a>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Wear it your way (wiyw)
 * -----------------------------------------------------------------------------
 * A component that showcases different ways to wear/style products.
 *
 * @param {Object} liquid - Liquid object.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.backgroundColor - Section background color.
 * @param {Array} blocks - Array of blocks.
 * @param {Object} blocks[].image - Block featured image.
 * @param {Array} blocks[].products - Block products (up to 2).
 * @param {Object} blocks[].product1Image - Block product 1 image.
 * @param {Object} blocks[].product2Image - Block product 2 image.
 * @param {String} blocks[].title - Block title.
 * @param {String} blocks[].text - Block text content.
 * @param {String} blocks[].linkUrl - Block link URL.
 * @param {String} blocks[].linkLabel - Block link label.
 *
 */
import { mapActions, mapState } from 'vuex'
import ResponsiveImage from '~global/images/responsive-image'
import ProductCard from '~async/product-card/product-card'
import IconInstagram from '~icons/social/instagram-new.svg'

export default {
  name: 'Wiyw',

  components: {
    ResponsiveImage,
    ProductCard,
    IconInstagram,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        backgroundColor: '',
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeBlock: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  methods: {
    ...mapActions({
      setQuickAddProduct: 'product-card/setQuickAddProduct',
    }),

    /**
     * Toggles the detail view for a block.
     * @param {String} id - The ID of the block to toggle.
     */
    toggleDetailView(id) {
      if (this.activeBlock === id) {
        this.activeBlock = null
        return
      }

      this.activeBlock = id
    },

    /**
     * - Sets the product handle in the quick view store.
     * - Emits an add-to-cart event.
     */
    handleAddToCart(product) {
      this.setQuickAddProduct(product)
      this.$emit('add-to-cart')
    },
  },
}
</script>

<style lang="scss">
@import './wiyw';
</style>